import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Fuel, TrendingUp, BarChart3, Building, DollarSign, LineChart, FileText, EyeOff, Check, X, Search, Filter, ArrowUpDown } from 'lucide-react';
import { useSupabase } from '../../hooks/useSupabase';
import { useAuthStore } from '../../store/useAuthStore';
import { retrieveUserValuations, hideValuation } from '../../services/valuationService';
import { SavedValuation, ValuationData } from '../../types/valuation';

export interface ValuationProperty {
  id: string;
  location: string;
  date: string;
  estimatedValue: {
    low: number;
    high: number;
    average: number;
  };
  metrics: {
    monthlyGallons: number;
    storeRevenue: number;
    storeSize: number;
    cstoreSalesPerSF: number;
    fuelMargin: number;
    totalGrossProfit: number;
  };
  assets: Record<string, number>;
  data?: ValuationData; // Fix data field type to use ValuationData instead of any
  hidden: boolean;
}

interface PreviouslyValuedPropertiesProps {
  showAll?: boolean;
}

export default function PreviouslyValuedProperties({ showAll = false }: PreviouslyValuedPropertiesProps) {
  const { supabase, client } = useSupabase();
  const { user, isAuthenticated } = useAuthStore();
  const [savedValuations, setSavedValuations] = useState<SavedValuation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isHiding, setIsHiding] = useState(false);
  const navigate = useNavigate();
  const [valuationToHide, setValuationToHide] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'value' | 'location'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6; // Show more items per page
  
  // Utility function for consistent currency formatting
  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else {
      return `$${Math.round(value / 1000)}K`;
    }
  };
  
  // Convert SavedValuation to ValuationProperty format
  const valuations: ValuationProperty[] = savedValuations.map(saved => {
    const data = saved.data;
    const estimatedValue = calculateEstimatedValue(data);
    const confidenceRange = estimatedValue * 0.15; // 15% range for low/high estimates
    
    // Extract all assets from the data
    const assetValues: Record<string, number> = {};
    if (data.assets) {
      // Calculate estimated values for each asset
      // Basic assumption: asset value is proportional to size and condition
      const assetRatios = {
        Fueling_Canopy: 0.06,
        UST: 0.12,
        Fueling_Equipment: 0.08,
        Site_Improvements: 0.09,
        Convenience_Store: 0.15,
        Service_Station: 0.20,
        Kiosk: 0.03,
        Food_Booth: 0.05,
        FFE: 0.04,
        Car_Wash: 0.12,
        Car_Wash_Equipment: 0.06
      };
      
      // Check if we have API response data with detailed breakdown
      if (data.apiResponse?.result) {
        const result = data.apiResponse.result;
        const assets = result.depreciated_assets || {};
        
        // 1. Extract land value directly from API
        if (result.predicted_land_value) {
          assetValues.Land_Value = Math.round(result.predicted_land_value / 10000) * 10000; // Round to nearest $10,000
        }
        
        // 2. Extract building components from depreciated assets
        if (assets.Convenience_Store?.Depreciated_Cost) {
          assetValues.Convenience_Store = Math.round(assets.Convenience_Store.Depreciated_Cost / 10000) * 10000;
        }
        
        if (assets.Service_Station?.Depreciated_Cost) {
          assetValues.Service_Station = Math.round(assets.Service_Station.Depreciated_Cost / 10000) * 10000;
        }
        
        if (assets.Kiosk?.Depreciated_Cost) {
          assetValues.Kiosk = Math.round(assets.Kiosk.Depreciated_Cost / 10000) * 10000;
        }
        
        if (assets.Food_Booth?.Depreciated_Cost) {
          assetValues.Food_Booth = Math.round(assets.Food_Booth.Depreciated_Cost / 10000) * 10000;
        }
        
        if (assets.Site_Improvements?.Depreciated_Cost) {
          assetValues.Site_Improvements = Math.round(assets.Site_Improvements.Depreciated_Cost / 10000) * 10000;
        }
        
        // 3. Extract equipment components from depreciated assets
        if (assets.Fueling_Equipment?.Depreciated_Cost) {
          assetValues.Fueling_Equipment = Math.round(assets.Fueling_Equipment.Depreciated_Cost / 10000) * 10000;
        }
        
        if (assets.Fueling_Canopy?.Depreciated_Cost) {
          assetValues.Fueling_Canopy = Math.round(assets.Fueling_Canopy.Depreciated_Cost / 10000) * 10000;
        }
        
        if (assets.UST?.Depreciated_Cost) {
          assetValues.UST = Math.round(assets.UST.Depreciated_Cost / 10000) * 10000;
        }
        
        if (assets.Car_Wash?.Depreciated_Cost) {
          assetValues.Car_Wash = Math.round(assets.Car_Wash.Depreciated_Cost / 10000) * 10000;
        }
        
        if (assets.Car_Wash_Equipment?.Depreciated_Cost) {
          assetValues.Car_Wash_Equipment = Math.round(assets.Car_Wash_Equipment.Depreciated_Cost / 10000) * 10000;
        }
        
        // 4. Extract FFE from depreciated assets
        if (assets.FFE?.Depreciated_Cost) {
          assetValues.FFE = Math.round(assets.FFE.Depreciated_Cost / 10000) * 10000;
        }
        
        // 5. Calculate the total of all physical assets
        const totalPhysicalAssets = Object.values(assetValues).reduce((sum, value) => sum + value, 0);
        
        // 6. Calculate residual business value (total business value - sum of physical assets)
        if (result.predicted_business_value) {
          const totalBusinessValue = result.predicted_business_value;
          // Calculate residual business value as the difference between total business value and physical assets
          const residualBusinessValue = totalBusinessValue - (totalPhysicalAssets - (assetValues.Land_Value || 0));
          
          // Only include if positive
          if (residualBusinessValue > 0) {
            assetValues.Business_Value = Math.round(residualBusinessValue / 10000) * 10000;
          }
        }
      } else {
        // If no API data is available, fall back to heuristic approximation
        // Process each asset if available
        Object.entries(data.assets).forEach(([key, asset]) => {
          if (asset) {
            // Calculate value based on condition factor and relative asset importance
            const conditionFactor = asset.condition === 'excellent' ? 1.2 :
                                 asset.condition === 'good' ? 1.0 :
                                 asset.condition === 'fair' ? 0.8 : 0.6;
            
            const baseRatio = (assetRatios as any)[key] || 0.05;
            assetValues[key] = Math.round((estimatedValue * baseRatio * conditionFactor) / 10000) * 10000; // Round to nearest $10,000
          }
        });
        
        // If no assets are available, use default approximation
        if (Object.keys(assetValues).length === 0) {
          assetValues.Land = Math.round((estimatedValue * 0.5) / 10000) * 10000;
          assetValues.Building = Math.round((estimatedValue * 0.3) / 10000) * 10000;
          assetValues.Equipment = Math.round((estimatedValue * 0.2) / 10000) * 10000;
        }
      }
    }
    
    return {
      id: saved.id,
      location: data.location || 'Unnamed Property',
      date: saved.lastModified,
      estimatedValue: {
        low: estimatedValue - confidenceRange,
        high: estimatedValue + confidenceRange,
        average: estimatedValue
      },
      metrics: {
        monthlyGallons: data.annualGallons ? data.annualGallons / 12 : 0,
        storeRevenue: data.storeRevenue || 0,
        storeSize: data.propertySize || 0,
        cstoreSalesPerSF: data.propertySize ? (data.storeRevenue || 0) / data.propertySize : 0,
        fuelMargin: data.fuelMargin || 0,
        totalGrossProfit: calculateTotalGrossProfit(data)
      },
      assets: assetValues,
      data: saved.data, // Include full valuation data
      hidden: saved.hidden || false
    };
  });

  useEffect(() => {
    const fetchValuations = async () => {
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        // Use client instead of supabase for direct database access
        // This follows the functional separation pattern where client is used for raw access
        const result = await retrieveUserValuations(client, user.id);
        
        if (result.success && result.data) {
          setSavedValuations(result.data);
        } else {
          setError(result.error || 'Failed to fetch valuations');
        }
      } catch (err) {
        console.error('Error fetching valuations:', err);
        setError('An error occurred while fetching your saved valuations.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchValuations();
  }, [client, user, isAuthenticated]);
  
  const handleHideValuation = async (valuationId: string) => {
    if (!user) return;
    
    setValuationToHide(valuationId);
    setIsHiding(true);
  };
  
  const confirmHide = async () => {
    if (!user || !valuationToHide) return;
    
    try {
      setLoading(true);
      // Use client instead of supabase following the singleton pattern
      const result = await hideValuation(client, valuationToHide, user.id);
      
      if (result.success) {
        // Update the hidden state of the valuation in local state
        setSavedValuations(prev => prev.map(v => v.id === valuationToHide ? { ...v, hidden: true } : v));
        setValuationToHide(null);
      } else {
        setError(result.error || 'Failed to hide valuation');
      }
    } catch (err) {
      console.error('Error hiding valuation:', err);
      setError('An error occurred while hiding the valuation.');
    } finally {
      setIsHiding(false);
      setLoading(false);
    }
  };

  const handleToggleSort = (field: 'date' | 'value' | 'location') => {
    if (sortBy === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc'); // Default to descending when changing sort field
    }
  };
  
  // Sort valuations based on current sort criteria
  const sortedValuations = [...valuations].sort((a, b) => {
    if (sortBy === 'date') {
      return sortDirection === 'asc' 
        ? new Date(a.date).getTime() - new Date(b.date).getTime()
        : new Date(b.date).getTime() - new Date(a.date).getTime();
    } else if (sortBy === 'value') {
      return sortDirection === 'asc'
        ? a.estimatedValue.average - b.estimatedValue.average
        : b.estimatedValue.average - a.estimatedValue.average;
    } else {
      // Location sorting
      return sortDirection === 'asc'
        ? a.location.localeCompare(b.location)
        : b.location.localeCompare(a.location);
    }
  });
  
  // Filter valuations based on search term
  const filteredValuations = sortedValuations.filter(valuation => 
    valuation.location.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Pagination
  const totalPages = Math.ceil(filteredValuations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedValuations = filteredValuations.slice(startIndex, startIndex + itemsPerPage);
  
  // Only display the first 3 valuations if not showing all
  const displayValuations = showAll ? paginatedValuations : filteredValuations.slice(0, 3);
  
  // Helper function to calculate estimated value
  function calculateEstimatedValue(data: ValuationData): number {
    const fuelRevenue = (data.annualGallons || 0) * (data.fuelMargin || 0);
    const storeRevenue = (data.storeRevenue || 0) * 12; // Annual
    const expenses = (data.operatingExpenses || 0) * 12; // Annual
    const netIncome = fuelRevenue + storeRevenue - expenses;
    
    // Base multiplier for gas station valuations
    const multiplier = 3.5;
    return netIncome * multiplier || 1500000; // Default if calculation is zero
  }
  
  // Helper function to calculate gross profit
  function calculateTotalGrossProfit(data: ValuationData): number {
    const fuelProfit = (data.annualGallons || 0) * (data.fuelMargin || 0) / 12;
    const storeProfit = (data.storeRevenue || 0) - (data.operatingExpenses || 0);
    return fuelProfit + storeProfit;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex flex-col space-y-4 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Previously Valued Properties</h2>
          </div>
          {!showAll && valuations.length >= 3 && (
            <Link to="/dashboard/valuations" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
              View All ({valuations.length})
            </Link>
          )}
        </div>
        
        {showAll && (
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-72">
              <input
                type="text"
                placeholder="Search by property location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
            
            <div className="flex gap-4 w-full md:w-auto">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleToggleSort('location')} 
                  className={`flex items-center text-sm font-medium px-3 py-2 rounded ${sortBy === 'location' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  Location
                  {sortBy === 'location' && <ArrowUpDown className="ml-1 h-3 w-3" />}
                </button>
                <button 
                  onClick={() => handleToggleSort('date')} 
                  className={`flex items-center text-sm font-medium px-3 py-2 rounded ${sortBy === 'date' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  Date
                  {sortBy === 'date' && <ArrowUpDown className="ml-1 h-3 w-3" />}
                </button>
                <button 
                  onClick={() => handleToggleSort('value')} 
                  className={`flex items-center text-sm font-medium px-3 py-2 rounded ${sortBy === 'value' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  Value
                  {sortBy === 'value' && <ArrowUpDown className="ml-1 h-3 w-3" />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Hide confirmation modal */}
      {isHiding && valuationToHide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-xl font-semibold mb-4">Hide Valuation</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to hide this property valuation?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setValuationToHide(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmHide}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium flex items-center"
              >
                <EyeOff className="w-4 h-4 mr-1" />
                Hide
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 flex justify-center">
          <div className="animate-pulse text-gray-400">Loading your valued properties...</div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
          {error}
        </div>
      ) : valuations.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No valued properties yet.</p>
          <p className="mt-2">Complete a property valuation to see your results here.</p>
          <Link 
            to="/valuation" 
            className="mt-4 inline-block bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-center text-sm font-medium"
          >
            Start New Valuation
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {displayValuations.map((valuation) => (
            <div 
              key={valuation.id} 
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200"
            >
              {/* Header */}
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {valuation.location}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Valued on {new Date(valuation.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right ml-6">
                    <p className="text-sm text-gray-500 mb-1">Total Estimated Value</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {formatCurrency(valuation.estimatedValue.average)}
                    </p>
                    <p className="text-sm text-gray-400">
                      Range: {formatCurrency(valuation.estimatedValue.low)} - {formatCurrency(valuation.estimatedValue.high)}
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHideValuation(valuation.id);
                      }}
                      disabled={isHiding}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-full transition-colors"
                      title="Hide valuation"
                    >
                      <EyeOff className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Action Button */}
                <button 
                  onClick={() => navigate(`/dashboard/performance-analysis/${valuation.id}`)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <BarChart3 className="w-5 h-5" />
                  View Details & Analysis
                </button>
              </div>
            </div>
          ))}
          
          {/* Pagination controls */}
          {showAll && totalPages > 1 && (
            <div className="flex justify-center mt-8">
              <nav className="flex items-center">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`mx-1 px-3 py-1 rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
                >
                  Previous
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`mx-1 px-3 py-1 rounded ${currentPage === page ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'}`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`mx-1 px-3 py-1 rounded ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
